import { useEffect, useRef, useState, type CSSProperties } from 'react';
import isMobile from 'is-mobile';
import classNames from 'classnames';
import { renderAsync } from 'docx-preview';
import { getDocument, GlobalWorkerOptions, TextLayer, type PDFDocumentProxy } from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export interface AnalysisReportModalData {
  content?: string;
  analysisId?: string;
  sourceFileUrl?: string;
  sourceFileName?: string;
  sourceFileType?: string;
  previewFileUrl?: string;
  previewFileType?: string;
}

const previewShellStyle: CSSProperties = {
  width: '100%',
  minHeight: '60vh',
  background: '#ffffff',
};

const previewStateStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  minHeight: 180,
  padding: 24,
  color: '#666a80',
  fontSize: 14,
};

const downloadButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 32,
  padding: '0 14px',
  borderRadius: 6,
  background: '#fa8846',
  color: '#ffffff',
  textDecoration: 'none',
};

export const AnalysisReportModalContent = ({ data }: { data?: AnalysisReportModalData }) => {
  return (
    <div
      className={classNames('markdown-body', isMobile() ? 'markdown-body-mobile' : '')}
      style={{
        maxHeight: '70vh',
        overflowY: 'auto',
      }}
    >
      {data?.sourceFileUrl ? (
        <AnalysisReportPreview data={data} />
      ) : (
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{data?.content}</ReactMarkdown>
      )}
    </div>
  );
};

const AnalysisReportPreview = ({ data }: { data: AnalysisReportModalData }) => {
  const fileType = String(data.sourceFileType || '').toLowerCase();
  const previewType = String(data.previewFileType || '').toLowerCase();
  const previewEndpoint = data.analysisId ? `/api/company/analysis/${data.analysisId}/public-preview` : '';

  if (!previewEndpoint) {
    return <UnsupportedPreview fileUrl={data.sourceFileUrl} fileType={fileType} />;
  }

  if (fileType === 'pdf') {
    return <PdfPreview previewEndpoint={previewEndpoint} sourceFileName={data.sourceFileName} sourceFileUrl={data.sourceFileUrl} />;
  }

  if (fileType === 'html' || previewType === 'html') {
    return (
      <HtmlPreview
        previewEndpoint={previewEndpoint}
        sourceFileName={data.sourceFileName}
        sourceFileUrl={data.sourceFileUrl}
      />
    );
  }

  if (fileType === 'docx') {
    return <DocxPreview previewEndpoint={previewEndpoint} sourceFileName={data.sourceFileName} sourceFileUrl={data.sourceFileUrl} />;
  }

  return <UnsupportedPreview fileUrl={data.sourceFileUrl} fileType={fileType} />;
};

const UnsupportedPreview = ({ fileUrl, fileType }: { fileUrl?: string; fileType?: string }) => (
  <div style={previewStateStyle}>
    <span>该报告格式暂不支持在线预览{fileType ? `：${fileType.toUpperCase()}` : ''}</span>
    {fileUrl ? (
      <a style={downloadButtonStyle} href={fileUrl} target="_blank" rel="noreferrer">
        下载报告
      </a>
    ) : null}
  </div>
);

const HtmlPreview = ({
  previewEndpoint,
  sourceFileName,
  sourceFileUrl,
}: {
  previewEndpoint: string;
  sourceFileName?: string;
  sourceFileUrl?: string;
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [srcDoc, setSrcDoc] = useState('');

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) {
      return;
    }
    const observer = new ResizeObserver(() => fitHtmlPreview(iframe));
    observer.observe(iframe);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    setSrcDoc('');

    const loadHtml = async () => {
      try {
        const response = await fetch(previewEndpoint, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to load HTML preview');
        }

        const html = await response.text();
        if (controller.signal.aborted) {
          return;
        }

        setSrcDoc(prepareHtmlPreviewDocument(html, sourceFileUrl));
        setStatus('ready');
      } catch (error) {
        if (!controller.signal.aborted) {
          setStatus('error');
        }
      }
    };

    void loadHtml();

    return () => {
      controller.abort();
    };
  }, [previewEndpoint, sourceFileUrl]);

  return (
    <div style={previewShellStyle}>
      {status === 'loading' ? <div style={previewStateStyle}>报告加载中...</div> : null}
      {status === 'error' ? <PreviewError label="HTML" fileUrl={sourceFileUrl} /> : null}
      <iframe
        ref={iframeRef}
        title={sourceFileName || '分析报告'}
        srcDoc={srcDoc}
        onLoad={(event) => fitHtmlPreview(event.currentTarget)}
        style={{ width: '100%', height: isMobile() ? '60vh' : '70vh', border: 0, display: 'block' }}
        sandbox="allow-same-origin allow-scripts"
        hidden={status !== 'ready'}
      />
    </div>
  );
};

const fitHtmlPreview = (iframe: HTMLIFrameElement) => {
  const doc = iframe.contentDocument;
  if (!doc?.body || !iframe.clientWidth) {
    return;
  }

  // Measure at the original scale so resizing never compounds the previous zoom.
  doc.documentElement.style.setProperty('zoom', '1');
  const availableWidth = Math.min(iframe.clientWidth, doc.documentElement.clientWidth);
  const contentWidth = Math.max(doc.documentElement.scrollWidth, doc.body.scrollWidth);
  doc.documentElement.style.setProperty('zoom', String(Math.min(1, availableWidth / contentWidth)));
};

const prepareHtmlPreviewDocument = (html: string, sourceFileUrl?: string) => {
  const baseHref = resolveHtmlBaseHref(sourceFileUrl);
  if (!baseHref) {
    return html;
  }

  const baseTag = `<base href="${escapeHtmlAttribute(baseHref)}">`;
  if (/<head[^>]*>/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>${baseTag}`);
  }
  if (/<!doctype[^>]*>/i.test(html)) {
    return html.replace(/<!doctype([^>]*)>/i, `<!doctype$1><head>${baseTag}</head>`);
  }
  return `<head>${baseTag}</head>${html}`;
};

const resolveHtmlBaseHref = (sourceFileUrl?: string) => {
  if (!sourceFileUrl) {
    return '';
  }

  try {
    const url = new URL(sourceFileUrl, window.location.href);
    url.hash = '';
    url.search = '';
    const slashIndex = url.pathname.lastIndexOf('/');
    url.pathname = slashIndex >= 0 ? url.pathname.slice(0, slashIndex + 1) : '/';
    return url.toString();
  } catch (error) {
    return '';
  }
};

const escapeHtmlAttribute = (value: string) => {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
};

const PdfPreview = ({
  previewEndpoint,
  sourceFileName,
  sourceFileUrl,
}: {
  previewEndpoint: string;
  sourceFileName?: string;
  sourceFileUrl?: string;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    container.innerHTML = '';
    setStatus('loading');

    const renderPdf = async () => {
      try {
        const response = await fetch(previewEndpoint, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to load PDF preview');
        }

        const data = await response.arrayBuffer();
        const pdf = await getDocument({ data }).promise;
        await renderPdfPages(pdf, container, () => cancelled);

        if (!cancelled) {
          setStatus('ready');
        }
      } catch (error) {
        if (!cancelled && !controller.signal.aborted) {
          setStatus('error');
        }
      }
    };

    void renderPdf();

    return () => {
      cancelled = true;
      controller.abort();
      container.innerHTML = '';
    };
  }, [previewEndpoint]);

  return (
    <div style={{ ...previewShellStyle, padding: isMobile() ? '16px 0' : '24px 24px 32px' }}>
      {status === 'loading' ? <div style={previewStateStyle}>报告加载中...</div> : null}
      {status === 'error' ? <PreviewError label="PDF" fileUrl={sourceFileUrl} /> : null}
      <div
        ref={containerRef}
        className="analysis-report-pdf-preview"
        aria-label={sourceFileName || 'PDF report preview'}
        hidden={status === 'error'}
      />
    </div>
  );
};

const renderPdfPages = async (pdf: PDFDocumentProxy, container: HTMLDivElement, isCancelled: () => boolean) => {
  const outputScale = Math.min(window.devicePixelRatio || 1, 2);
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    if (isCancelled()) {
      return;
    }

    const page = await pdf.getPage(pageNumber);
    const baseViewport = page.getViewport({ scale: 1 });
    const availableWidth = Math.max(container.clientWidth, 320);
    const scale = availableWidth / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const pageLayer = document.createElement('div');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas is not supported');
    }

    pageLayer.className = 'analysis-report-pdf-page-layer';
    pageLayer.style.width = `${viewport.width}px`;
    pageLayer.style.height = `${viewport.height}px`;
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    canvas.className = 'analysis-report-pdf-page';
    await page.render({
      canvas,
      canvasContext: context,
      viewport,
      transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
    }).promise;
    const textLayerContainer = document.createElement('div');
    textLayerContainer.className = 'analysis-report-pdf-text-layer';
    pageLayer.appendChild(canvas);
    pageLayer.appendChild(textLayerContainer);
    const textLayer = new TextLayer({
      textContentSource: page.streamTextContent(),
      container: textLayerContainer,
      viewport,
    });
    await textLayer.render();

    if (!isCancelled()) {
      container.appendChild(pageLayer);
    }
  }
};

const DocxPreview = ({
  previewEndpoint,
  sourceFileName,
  sourceFileUrl,
}: {
  previewEndpoint: string;
  sourceFileName?: string;
  sourceFileUrl?: string;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    container.innerHTML = '';
    setStatus('loading');

    const renderDocx = async () => {
      try {
        const response = await fetch(previewEndpoint, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to load DOCX preview');
        }

        const blob = await response.blob();
        if (cancelled || !containerRef.current) {
          return;
        }

        await renderAsync(blob, containerRef.current, undefined, {
          className: 'docx',
          inWrapper: false,
          ignoreWidth: true,
          ignoreHeight: true,
          breakPages: false,
          useBase64URL: true,
        });

        if (!cancelled) {
          setStatus('ready');
        }
      } catch (error) {
        if (!cancelled && !controller.signal.aborted) {
          setStatus('error');
        }
      }
    };

    void renderDocx();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [previewEndpoint]);

  return (
    <div style={{ ...previewShellStyle, padding: isMobile() ? '16px 0' : '24px 24px 32px' }}>
      {status === 'loading' ? <div style={previewStateStyle}>报告加载中...</div> : null}
      {status === 'error' ? <PreviewError label="DOCX" fileUrl={sourceFileUrl} /> : null}
      <div
        ref={containerRef}
        className="analysis-report-docx-preview"
        aria-label={sourceFileName || 'DOCX report preview'}
        hidden={status === 'error'}
      />
    </div>
  );
};

const PreviewError = ({ label, fileUrl }: { label: string; fileUrl?: string }) => (
  <div style={previewStateStyle}>
    <span>{label} 预览加载失败，请下载后查看。</span>
    {fileUrl ? (
      <a style={downloadButtonStyle} href={fileUrl} target="_blank" rel="noreferrer">
        下载报告
      </a>
    ) : null}
  </div>
);
